import { FC, useState, MouseEvent } from "react";
import {
  useGridApiContext,
  useGridSelector,
  gridSortModelSelector,
} from "@mui/x-data-grid";
import {
  Box,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ImportExport from "@mui/icons-material/ImportExport";
import { grey } from "@mui/material/colors";

export type CareTeamSortField = "attending_provider" | "service";

const CARE_TEAM_FIELD = "care_team";

type SortDirection = "asc" | "desc";

const SORT_OPTIONS: {
  field: CareTeamSortField;
  direction: SortDirection;
  label: string;
}[] = [
  {
    field: "attending_provider",
    direction: "asc",
    label: "Attending provider (A to Z)",
  },
  {
    field: "attending_provider",
    direction: "desc",
    label: "Attending provider (Z to A)",
  },
  { field: "service", direction: "asc", label: "Service (A to Z)" },
  { field: "service", direction: "desc", label: "Service (Z to A)" },
];

export interface CareTeamSortConfig {
  sortBy: CareTeamSortField;
  setSortBy: (field: CareTeamSortField) => void;
}

interface CareTeamColumnHeaderProps {
  sortBy: CareTeamSortField;
  onSortByChange: (field: CareTeamSortField) => void;
}

export const CareTeamColumnHeader: FC<CareTeamColumnHeaderProps> = ({
  sortBy,
  onSortByChange,
}) => {
  const apiRef = useGridApiContext();
  const sortModel = useGridSelector(apiRef, gridSortModelSelector);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const careTeamSort = sortModel.find((s) => s.field === CARE_TEAM_FIELD);
  const sortDirection = careTeamSort?.sort ?? null;

  const isOptionActive = (field: CareTeamSortField, direction: SortDirection) =>
    Boolean(careTeamSort) &&
    sortBy === field &&
    sortDirection === direction;

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const clearCareTeamSort = () => {
    const next = sortModel.filter((s) => s.field !== CARE_TEAM_FIELD);
    apiRef.current.setSortModel(next);
    handleCloseMenu();
  };

  const applySort = (field: CareTeamSortField, direction: SortDirection) => {
    onSortByChange(field);
    apiRef.current.setSortModel([{ field: CARE_TEAM_FIELD, sort: direction }]);
    handleCloseMenu();
  };

  const handleOptionClick = (
    field: CareTeamSortField,
    direction: SortDirection
  ) => {
    if (isOptionActive(field, direction)) {
      clearCareTeamSort();
      return;
    }
    applySort(field, direction);
  };

  const SortIcon =
    sortDirection === "asc"
      ? ArrowUpward
      : sortDirection === "desc"
        ? ArrowDownward
        : ImportExport;

  return (
    <>
      <Tooltip title="Sort Care Team">
        <Box
          component="button"
          type="button"
          id="care-team-sort-button"
          aria-label="Sort Care Team"
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={handleOpenMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            minHeight: 56,
            boxSizing: "border-box",
            pr: 0.25,
            border: "none",
            background: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            font: "inherit",
            color: "inherit",
            textAlign: "left",
          }}
        >
          <Box
            component="span"
            sx={{
              color: grey[600],
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            Care Team
          </Box>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              color: "action.active",
              alignItems: "center",
            }}
            aria-hidden
          >
            <SortIcon fontSize="small" />
          </Box>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          list: {
            dense: true,
            "aria-labelledby": "care-team-sort-button",
          },
        }}
      >
        {SORT_OPTIONS.map(({ field, direction, label }) => {
          const active = isOptionActive(field, direction);
          return (
            <MenuItem
              key={`${field}-${direction}`}
              onClick={() => handleOptionClick(field, direction)}
              selected={active}
            >
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          disabled={!careTeamSort}
          onClick={clearCareTeamSort}
        >
          <ListItemText primary="Clear sort" />
        </MenuItem>
      </Menu>
    </>
  );
};
